import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, isToday, isTomorrow, isPast, parseISO, isValid } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { ClipboardCheck, Plus, Calendar as CalendarIcon, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  high: 'bg-red-50 text-red-600 border border-red-100',
  medium: 'bg-amber-50 text-amber-600 border border-amber-100',
  low: 'bg-green-50 text-green-600 border border-green-100',
};

function dueLabel(dueDate) {
  if (!dueDate) return null;
  const d = parseISO(dueDate);
  if (!isValid(d)) return null;
  if (isToday(d)) return { text: 'Due today', overdue: false };
  if (isTomorrow(d)) return { text: 'Due tomorrow', overdue: false };
  if (isPast(d)) return { text: `Overdue · ${format(d, 'MMM d')}`, overdue: true };
  return { text: format(d, 'MMM d'), overdue: false };
}

export const TasksWidget = () => {
  const { getAuthHeader } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`, getAuthHeader());
      setTasks(res.data || []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const addTask = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/tasks`, {
        title: title.trim(),
        priority,
        due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
      }, getAuthHeader());
      setTasks((prev) => [res.data, ...prev]);
      setTitle('');
      setPriority('medium');
      setDueDate(null);
      setAdding(false);
    } catch {
      toast.error('Could not add task');
    } finally {
      setSaving(false);
    }
  };

  const toggleTask = async (task) => {
    const next = !task.completed;
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: next } : t)));
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { completed: next }, getAuthHeader());
    } catch {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: !next } : t)));
      toast.error('Could not update task');
    }
  };

  const removeTask = async (task) => {
    const prev = tasks;
    setTasks((p) => p.filter((t) => t.id !== task.id));
    try {
      await axios.delete(`${API}/tasks/${task.id}`, getAuthHeader());
    } catch {
      setTasks(prev);
      toast.error('Could not delete task');
    }
  };

  return (
    <div
      className="rounded-[20px] bg-white p-6 shadow-[0px_18px_40px_rgba(112,144,176,0.12)]"
      data-testid="tasks-widget"
    >
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
            <ClipboardCheck className="h-5 w-5 text-blue-600" strokeWidth={2} />
          </div>
          <h3 className="font-['Manrope'] text-lg font-bold text-[#2B3674]">Tasks &amp; Priorities</h3>
        </div>
        <button
          onClick={() => setAdding((v) => !v)}
          data-testid="add-task-button"
          className="flex items-center gap-1 rounded-full bg-[#F4F7FE] px-3 py-1.5 text-xs font-semibold text-[#2B3674] transition-colors hover:bg-[#E9EDF7]"
        >
          {adding ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {adding ? 'Cancel' : 'Add task'}
        </button>
      </div>

      {adding && (
        <div className="mb-4 space-y-2 rounded-2xl bg-[#F4F7FE] p-3" data-testid="add-task-form">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
            placeholder="What needs to get done?"
            className="border-0 bg-white text-sm text-[#2B3674] shadow-sm placeholder:text-[#A3AED0]"
            data-testid="task-title-input"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="h-9 w-[110px] border-0 bg-white text-xs font-medium text-[#2B3674] shadow-sm" data-testid="task-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex h-9 items-center gap-1.5 rounded-md bg-white px-3 text-xs font-medium text-[#2B3674] shadow-sm"
                  data-testid="task-due-date-button"
                >
                  <CalendarIcon className="h-3.5 w-3.5 text-[#A3AED0]" />
                  {dueDate ? format(dueDate, 'MMM d') : 'Due date'}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <CalendarComponent mode="single" selected={dueDate} onSelect={setDueDate} />
              </PopoverContent>
            </Popover>
            <button
              onClick={addTask}
              disabled={saving || !title.trim()}
              data-testid="task-save-button"
              className="ml-auto rounded-md bg-gradient-to-r from-[#2447D6] to-[#1E3FBF] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-opacity disabled:opacity-40"
            >
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#2447D6] border-t-transparent" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-10 text-center" data-testid="tasks-empty-state">
          <ClipboardCheck className="mx-auto mb-2 h-10 w-10 text-[#E9EDF7]" />
          <p className="text-sm text-[#A3AED0]">No tasks yet — add your first one</p>
        </div>
      ) : (
        <div className="max-h-[360px] space-y-1 overflow-y-auto pr-1">
          {tasks.map((task) => {
            const due = dueLabel(task.due_date);
            return (
              <div
                key={task.id}
                className="group flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-[#F4F7FE]"
                data-testid={`task-row-${task.id}`}
              >
                <Checkbox
                  checked={!!task.completed}
                  onCheckedChange={() => toggleTask(task)}
                  className="h-[18px] w-[18px] rounded-[5px] border-[#A3AED0] data-[state=checked]:border-[#2447D6] data-[state=checked]:bg-[#2447D6]"
                  data-testid="task-complete-checkbox"
                />
                <span
                  className={`flex-1 truncate text-sm font-medium ${
                    task.completed ? 'text-[#A3AED0] line-through' : 'text-[#2B3674]'
                  }`}
                >
                  {task.title}
                </span>
                {task.suggested && (
                  <span
                    className="flex shrink-0 items-center gap-1 rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-[10.5px] font-semibold text-violet-600"
                    data-testid="task-suggested-badge"
                  >
                    <Sparkles className="h-3 w-3" /> Suggested
                  </span>
                )}
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold capitalize ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium}`}>
                  {task.priority}
                </span>
                {due && (
                  <span className={`hidden shrink-0 text-xs sm:block ${due.overdue ? 'font-semibold text-red-500' : 'text-[#A3AED0]'}`}>
                    {due.text}
                  </span>
                )}
                <button
                  onClick={() => removeTask(task)}
                  data-testid="task-delete-button"
                  className="rounded-md p-1 text-[#A3AED0] opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
